import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { Box, Grid, Typography, Divider } from '@mui/material';
import trashIcon from '../../../../assets/images/trashIcon.svg';
import { Field, Form, Formik, FieldArray, FastField } from 'formik';
import { useLoading } from 'hooks/useLoading';
import yup from 'utils/validation';
import IDestination from 'interfaces/IDestination';
import DestinationService from 'services/DestinationService';
import MainTitle from 'components/MainTitle';
import Heading from 'components/Heading';
import ErrorMessage from 'components/ErrorMessage';
import Button from 'components/Button';
import UploadImage from 'components/UploadImage/field';
import { Container } from './styles';
import ImageService from 'services/ImageService';
import MessageModal from 'common/MessageModal';
import TextArea from 'components/TextArea';
import BannerUploadField from 'components/UploadImage/BannerUploadField';
import { SettingsListItemButton } from 'components/List/styles';

const Destination = (): JSX.Element => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { openLoading, closeLoading } = useLoading();
  const [destination, setDestination] = useState<IDestination | undefined>();
  const [showModal, setShowModal] = useState(false);
  const [messageInfo, setMessageInfo] = useState({ title: '', message: '' });
  const [formValues, setFormValues] = useState({
    title: '',
    image: [''],
    characteristics: '',
    banners: [{ description: '', image: '' }],
  });
  const service = new DestinationService();
  const imageService = new ImageService();

  useEffect(() => {
    openLoading();
    fetchDestination().finally(() => closeLoading());
  }, []);

  const fetchDestination = async () => {
    const destinationId = Number(id);
    if (Number.isNaN(destinationId)) return;

    const data = await service.findOne(destinationId);
    setDestination(data);

    setFormValues({
      title: data.title,
      image: [data.image],
      characteristics: data?.characteristics.join('\n'),
      banners: data.banners?.length
        ? data.banners.map(b => ({ description: b.description || '', image: b.image }))
        : [{ description: '', image: '' }],
    });
  };

  const validationSchema = yup.object({
    title: yup.string().required(),
    characteristics: yup.string().required(),
    image: yup.array().min(1).required(),
    banners: yup
      .array()
      .of(
        yup.object().shape({
          description: yup.string().required('Descrição do banner obrigatória'),
          image: yup.mixed().required('Imagem do banner obrigatória'),
        })
      )
      .min(1, 'Adicione pelo menos um banner'),
  });

  const onSubmit = async (values: any) => {
    if (!destination?.id) return;
    openLoading();

    try {
      const [cardImage] = await imageService.uploadMultiple(values.image);
      const processedBanners = await Promise.all(
        values.banners.map(async (banner: any) => {
          let imageUrl = banner.image;

          if (typeof banner.image !== 'string') {
            const uploaded = await imageService.uploadMultiple([banner.image]);
            imageUrl = uploaded[0];
          }

          return {
            id: 0,
            description: banner.description,
            image: imageUrl,
          };
        })
      );

      await service.update(destination?.id, {
        title: values.title,
        image: cardImage,
        link: '',
        characteristics: values.characteristics.split('\n'),
        banners: processedBanners,
      });

      setShowModal(true);
      const messageWithSpan = 'O destino foi <span>editado</span> com sucesso.';
      setMessageInfo({
        title: 'Sucesso',
        message: messageWithSpan,
      });
    } catch (error) {
      setShowModal(true);
      const messageWithSpan = 'Erro ao <span>editar</span> destino.';
      setMessageInfo({
        title: 'Erro',
        message: messageWithSpan,
      });
    }

    closeLoading();
  };

  const closeModal = () => {
    setShowModal(false);
    if (messageInfo.title === 'Sucesso') {
      history.push('/admin/config');
    }
  };

  return (
    <Container>
      <MainTitle>Configurações</MainTitle>
      <div style={{ padding: '0 50px' }}>
        <Heading>Editar Destino</Heading>
        <Formik initialValues={formValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize>
          {({ values }) => (
            <Form>
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Informações do Card
                    </Typography>
                  </Grid>

                  <Grid item xs={12} className="form-group">
                    <label>Nome do Destino</label>
                    <Field name="title" className="input-field" />
                    <ErrorMessage name="title" />
                  </Grid>

                  <Grid item xs={12} className="form-group">
                    <TextArea name="characteristics" maxLength={250} title="Descrição do card" />
                    <ErrorMessage name="characteristics" />
                  </Grid>
                  <Grid item xs={12} className="form-group">
                    <UploadImage
                      name="image"
                      descriptionText="Imagem do Card: Adicione 1 imagem. Para garantir a qualidade da visualização, a imagem precisa ter a dimensão mínima de 1366 x 600 pixels"
                      multipleFiles={false}
                      htmlFor="destination-image"
                      limitFiles={1}
                    />
                    <ErrorMessage name="image" />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider style={{ margin: '20px 0' }} />
                  </Grid>

                  <Grid item xs={12}>
                    <FieldArray name="banners">
                      {({ push, remove }) => (
                        <>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">Imagens do Carrossel</Typography>

                            <Button
                              type="button"
                              onClick={() => push({ description: '', image: '' })}
                              style={{ width: 'auto' }}>
                              Adicionar
                            </Button>
                          </Box>

                          {values.banners.map((_, index) => (
                            <Box
                              key={index}
                              mb={4}
                              p={3}
                              border="1px solid #e0e0e0"
                              borderRadius="8px"
                              position="relative"
                              bgcolor="#f9f9f9">
                              <Box position="absolute" right={10} top={10}>
                                <SettingsListItemButton
                                  onClick={() => remove(index)}
                                  disabled={values.banners.length === 1}>
                                  <img src={trashIcon} alt="Excluir" />
                                </SettingsListItemButton>
                              </Box>

                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <label style={{ fontWeight: 'bold' }}>Banner #{index + 1}</label>
                                </Grid>

                                <Grid item xs={12}>
                                  <TextArea
                                    name={`banners.${index}.description`}
                                    maxLength={470}
                                    title="Descrição do Banner"
                                  />
                                  <ErrorMessage name={`banners.${index}.description`} />
                                </Grid>

                                <Grid item xs={12}>
                                  <FastField
                                    name={`banners.${index}.image`}
                                    component={BannerUploadField}
                                    // Props extras são passadas automaticamente para o componente
                                    htmlFor={`banner-upload-${index}`}
                                    descriptionText="Imagem do Carrossel: Para garantir a qualidade da visualização, a imagem precisa ter a dimensão mínima de 1366 x 600 pixels"
                                  />
                                  <ErrorMessage name={`banners.${index}.image`} />
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                        </>
                      )}
                    </FieldArray>
                  </Grid>

                  <Grid item xs={12} mt={2}>
                    <Grid container direction="row" justifyContent="flex-end" alignItems="center" columnSpacing={4}>
                      <Grid item>
                        <Button buttonType="secondary" type="button" onClick={() => history.goBack()}>
                          Cancelar
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button type="submit">Salvar</Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Form>
          )}
        </Formik>
      </div>
      <MessageModal isOpen={showModal} title={messageInfo.title} message={messageInfo.message} onClose={closeModal} />
    </Container>
  );
};

export default Destination;
